import {
  Component,
  OnInit,
  ViewEncapsulation,
  ChangeDetectionStrategy,
  inject,
  computed,
  output,
  viewChild,
} from '@angular/core';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AppSettings, Settings } from 'app/app.settings';
import { MenuService } from '../menu/menu.service';
import { Menu } from '../menu/menu.model';
import { AuthService } from '@core/auth';
import { ConfigService, SystemService } from '@core/services';
import { ProgramsStore } from '@core/stores';
import * as Entities from '@shared/entities';
import * as Models from '@shared/models';
import { Enums, IEnums } from 'app/app.enums';
import { rotate } from '../utils/app-animation';

@Component({
  selector: 'app-sidenav',
  templateUrl: './sidenav.component.html',
  styleUrls: ['./sidenav.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: [MenuService],
  animations: [rotate],
  standalone: true,
  imports: [RouterLink, RouterLinkActive, MatButtonModule, MatIconModule, MatMenuModule, MatTooltipModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SidenavComponent implements OnInit {
  private readonly appSettings = inject(AppSettings);
  private readonly authService = inject(AuthService);
  private readonly menuService = inject(MenuService);
  private readonly systemService = inject(SystemService);
  private readonly configService = inject(ConfigService);
  private readonly programsStore = inject(ProgramsStore);

  readonly reportMenuTrigger = viewChild<MatMenuTrigger>('reportMenuTrigger');
  readonly mgmtMenuTrigger = viewChild<MatMenuTrigger>('mgmtMenuTrigger');

  // Computed signals from config - no manual change detection needed!
  private readonly displayInboundDashboard = computed(
    () => this.configService.config()?.displayInboundDashboard ?? false
  );
  private readonly displayOutboundDashboard = computed(
    () => this.configService.config()?.displayOutboundDashboard ?? false
  );

  readonly programs = computed(() =>
    this.programsStore.records().filter((p) => p.displayInNav === true && (p.navRoute || p.slug))
  );

  collapsedTooltipText = '';
  currentVersion = '';
  enums: IEnums = Enums;
  firstName = '';
  lastName = '';
  menuItems: Menu[] = [];
  parentMenu: Menu[] = [];
  rotateMenuGlyph = false;
  settings: Settings;
  systemConfiguration!: Models.ISystemConfiguration;
  translationInfo!: Models.ITranslationInfo;

  private isDesktop = true;

  // Modern output signal
  readonly collapse = output<boolean>();
  isCollapsed = false;

  constructor() {
    this.settings = this.appSettings.settings;
    this.collapsedTooltipText = this.appSettings.settings.menuType;
  }

  ngOnInit(): void {
    this.programsStore.loadPrograms();
    this.menuItems = this.menuService.getVerticalMenuItems();
    this.parentMenu = this.menuItems.filter((item) => item.parentId === 0);
  }

  isInRole(role: string): boolean {
    return this.authService.isInRole(role);
  }

  canAccessLeads(): boolean {
    return (
      this.authService.isInRole(this.enums.role.shiftAdmin) ||
      this.authService.isInRole(this.enums.role.shiftAnalyst) ||
      this.authService.isInRole(this.enums.role.corporate)
    );
  }

  // TODO: Remove this once we validate dynamic navigation
  shouldDisplayInboundDashboard(): boolean {
    return (
      (this.authService.isInProgram(this.enums.program.inboundService) ||
        this.authService.isInRole(this.enums.role.shiftAdmin) ||
        this.authService.isInRole(this.enums.role.shiftAnalyst) ||
        this.authService.isInRole(this.enums.role.conciergeManager) ||
        this.authService.isInRole(this.enums.role.conciergeAgent) ||
        this.authService.isInRole(this.enums.role.corporate)) &&
      this.displayInboundDashboard()
    );
  }

  // TODO: Remove this once we validate dynamic navigation
  shouldDisplayOutboundDashboard(): boolean {
    return (
      (this.authService.isInProgram(this.enums.program.recallConcierge) ||
        this.authService.isInRole(this.enums.role.shiftAdmin) ||
        this.authService.isInRole(this.enums.role.shiftAnalyst) ||
        this.authService.isInRole(this.enums.role.conciergeManager) ||
        this.authService.isInRole(this.enums.role.conciergeAgent) ||
        this.authService.isInRole(this.enums.role.corporate)) &&
      this.displayOutboundDashboard()
    );
  }

  // TODO: Remove this once we validate dynamic navigation
  shouldDisplayConnectedDashboard(): boolean {
    return (
      this.authService.isInProgram(5) ||
      this.authService.isInRole(this.enums.role.shiftAdmin) ||
      this.authService.isInRole(this.enums.role.shiftAnalyst) ||
      this.authService.isInRole(this.enums.role.conciergeManager) ||
      this.authService.isInRole(this.enums.role.conciergeAgent) ||
      this.authService.isInRole(this.enums.role.corporate)
    );
  }

  shouldDisplayProgramInNav(program: Entities.Program): boolean {
    return (
      this.authService.isInProgram(program.programId) ||
      this.authService.isInRole(this.enums.role.shiftAdmin) ||
      this.authService.isInRole(this.enums.role.shiftAnalyst) ||
      this.authService.isInRole(this.enums.role.conciergeManager) ||
      this.authService.isInRole(this.enums.role.conciergeAgent) ||
      this.authService.isInRole(this.enums.role.corporate)
    );
  }

  getProgramRoute(program: Entities.Program): string[] {
    // Use the route from the program entity
    // NavRoute should be stored as a path like '/dashboard/connected' or '/dashboard/inbound'
    // If NavRoute is not set, construct route from Slug: '/dashboard/{slug}'
    let route: string;
    if (program.navRoute) {
      route = program.navRoute;
    } else if (program.slug) {
      route = `/dashboard/${program.slug}`;
    } else {
      return [];
    }
    // Split by '/' and filter out empty strings to create route array
    return route.split('/').filter((segment) => segment.length > 0);
  }

  getProgramDisplayName(program: Entities.Program): string {
    // Use DisplayName if available, otherwise fall back to Name
    return program.displayName || program.name;
  }

  getProgramIcon(program: Entities.Program): string {
    // Use Icon if available, otherwise fall back to default 'dashboard' icon
    return program.icon || 'dashboard';
  }

  openReportingMenu(): void {
    const menu = document.getElementById('reporting');
    const mgmtTrigger = this.mgmtMenuTrigger();
    const reportTrigger = this.reportMenuTrigger();
    if (
      this.settings.menuType === this.enums.menuType.mini &&
      this.isDesktop &&
      menu &&
      menu.children[0] &&
      !menu.children[0].classList.contains('expanded')
    ) {
      if (mgmtTrigger?.menuOpen) {
        mgmtTrigger.closeMenu();
      }
      reportTrigger?.openMenu();
      setTimeout(() => {
        if (reportTrigger?.menuOpen) {
          reportTrigger.closeMenu();
        }
      }, 5000);
    }
  }

  viewReportingSubMenu(): void {
    this.closeSubMenus();
    const menu = document.getElementById('reporting');
    if (menu && menu.children[0] && menu.children[1]) {
      if (menu.children[0].classList.contains('expanded')) {
        menu.children[0].classList.remove('expanded');
        menu.children[1].classList.remove('show');
      } else {
        menu.children[0].classList.add('expanded');
        menu.children[1].classList.add('show');
      }
    }
    this.mgmtMenuTrigger()?.closeMenu();
  }

  openManagementMenu(): void {
    this.closeSubMenus();
    const menu = document.getElementById('managementTools');
    const reportTrigger = this.reportMenuTrigger();
    const mgmtTrigger = this.mgmtMenuTrigger();
    if (
      this.settings.menuType === this.enums.menuType.mini &&
      this.isDesktop &&
      menu &&
      menu.children[0] &&
      !menu.children[0].classList.contains('expanded')
    ) {
      if (reportTrigger?.menuOpen) {
        reportTrigger.closeMenu();
      }
      mgmtTrigger?.openMenu();
      setTimeout(() => {
        if (mgmtTrigger?.menuOpen) {
          mgmtTrigger.closeMenu();
        }
      }, 5000);
    }
  }

  viewManagementSubMenu(): void {
    this.closeSubMenus();
    const menu = document.getElementById('managementTools');
    if (menu && menu.children[0] && menu.children[1]) {
      if (menu.children[0].classList.contains('expanded')) {
        menu.children[0].classList.remove('expanded');
        menu.children[1].classList.remove('show');
      } else {
        menu.children[0].classList.add('expanded');
        menu.children[1].classList.add('show');
      }
    }
    this.mgmtMenuTrigger()?.closeMenu();
  }

  closeSubMenus(): void {
    const menu = document.getElementById('vertical-menu');
    if (menu) {
      for (let i = 0; i < menu.children.length; i++) {
        const child = menu.children[i]; /*.children[0]*/
        if (child && child.children[0] && child.children[1]) {
          if (child.children[0].classList.contains('expanded')) {
            child.children[0].classList.remove('expanded');
            child.children[1].classList.remove('show');
          }
        }
      }
    }
  }

  collapsedLeftSidebar(): void {
    if (this.settings.menuType === this.enums.menuType.default) {
      this.systemService.changeMenuType(this.enums.menuType.mini);
    } else if (this.settings.menuType === this.enums.menuType.mini) {
      this.systemService.changeMenuType(this.enums.menuType.default);
    }
    this.rotateMenuGlyph = !this.rotateMenuGlyph;
  }

  onClick(menuId: number): void {
    this.menuService.toggleMenuItem(menuId);
    this.menuService.closeOtherSubMenus(this.menuItems, menuId);
  }

  toggleSidenavCollapse(): void {
    this.isCollapsed = !this.isCollapsed;
    this.collapse.emit(this.isCollapsed);

    // Update all menu items to show/hide text
    const menuItems = document.querySelectorAll('.menu-item .mat-mdc-button');
    menuItems.forEach((item) => {
      if (this.isCollapsed) {
        item.classList.add('collapsed');
      } else {
        item.classList.remove('collapsed');
      }
    });
  }
}
